import { Users } from '@platform/client';
import ProfileDropdown from '../dashboard/ProfileDropdown';
import OrganizationDropdown from '../dashboard/OrganizationDropdown';
import { ProfessionalType } from '@platform/client/schemas';
import { ReactElement } from 'react';
import { EYLogo, F5Logo, Logo, ReckittLogo, SAPLogo } from '@platform/components';
import { LumenDashboardLogo } from '../dashboard/home/LumenDashboardLogo';
import dataTestids from '../constants/test-ids';
import cx from 'classix';
import FlexibleLink from '../util/FlexibleLink';

/* Maps types of professional to the logo displayed. No logo displayed causes us to show
 * the full MOVINGWORLDS logo instead of the condensed version. Some variants here are not
 * used and only exist to satisfy all cases of ProfessionalType. */
const CORPORATE_CUSTOMER_LOGO: Record<ProfessionalType, ReactElement | null> = {
  mwi: null, // Not used
  sap: (
    // Unfortunately I'm hardcoding these width for expedience; would be great
    // to revisit and have them auto size with height constrained to
    // the navbar
    <div style={{ width: '97px' }}>
      <SAPLogo />
    </div>
  ),
  ey: (
    <div style={{ width: '42px' }}>
      <EYLogo />
    </div>
  ),
  f5: (
    <div style={{ width: '48px' }}>
      <F5Logo />
    </div>
  ),
  generic: null,
  kering: null, // Not used
  paypal: null, // Not used
  reckitt: (
    <div style={{ width: '82px' }}>
      <ReckittLogo />
    </div>
  ),
  tsh: null,
  wmp: null, // Not used
  lumen: (
    <div className='-ml-4' style={{ width: '146px' }}>
      <LumenDashboardLogo />
    </div>
  ),
};

/**
 * The top navbar just below the main site blue navbar, containing the user's organization name, logo, and user dropdown
 */
const DashboardHeader = () => {
  const { data: user } = Users.useGetCurrentUser();

  const corporateCustomerLogo =
    user?.accountType === 'experteer' && CORPORATE_CUSTOMER_LOGO[user.professionalType];

  return (
    <div
      className={cx(
        'sticky top-0 z-40 md:relative md:top-auto bg-white px-4',
        'lg:px-10 py-4 flex w-full gap-2 md:gap-4 min-w-0 md:h-[var(--navbar-height)] items-center',
        'border-b-2 border-b-gray-100 whitespace-nowrap',
      )}
      data-testid={dataTestids.DASHBOARD.HEADER}
    >
      <div className='flex h-full items-center gap-6'>
        <div
          className={cx(
            'flex h-full items-center',
            corporateCustomerLogo ? 'w-[113px]' : 'w-[224px]',
          )}
        >
          <a
            className='contents'
            href='/platform' // Redirects to '/'
            data-testid={dataTestids.LAYOUT.HEADER.LOGO}
          >
            <Logo type='mw-text' variant={corporateCustomerLogo ? 'blue-wrapped' : 'blue'} />
          </a>
        </div>
        {corporateCustomerLogo && <div className='w-0.5 h-full bg-gray-100' />}
        {corporateCustomerLogo && (
          <div className='flex h-full items-center'>{corporateCustomerLogo}</div>
        )}
      </div>
      <div className='ml-auto flex min-w-0 flex-col md:flex-row md:gap-2 items-start md:items-center'>
        {user?.accountType === 'organization' && <OrganizationDropdown />}
        {user?.cohort?.url && (
          <FlexibleLink
            className='font-bold underline-offset-8 decoration-2 hover:underline hover:text-blue-600'
            href={user.cohort.url}
            type='external'
          >
            {user.cohort.displayName} Cohort
          </FlexibleLink>
        )}
        <ProfileDropdown />
      </div>
    </div>
  );
};

export default DashboardHeader;
